import type { LessonVisual } from "../types";

function splitList(text: string): string[] {
  return text
    .replace(/\.$/, "")
    .split(/,|;| vs\.?\s| versus | and /i)
    .map((s) => s.trim())
    .filter(Boolean);
}

function isValidItemSet(items: string[]): boolean {
  return (
    items.length >= 2 &&
    items.length <= 6 &&
    items.every((item) => item.length > 0 && item.length <= 45)
  );
}

function extractItems(visual: LessonVisual): string[] {
  const colonIndex = visual.description.lastIndexOf(":");
  if (colonIndex !== -1) {
    const fromDescription = splitList(visual.description.slice(colonIndex + 1));
    if (isValidItemSet(fromDescription)) return fromDescription;
  }
  const fromTitle = splitList(visual.title.replace(/\([^)]*\)/g, ""));
  if (isValidItemSet(fromTitle)) return fromTitle;
  return [];
}

const TYPE_LABEL: Record<string, string> = {
  diagram: "Diagram",
  "process-flow": "Process flow",
  timeline: "Timeline",
  comparison: "Comparison",
  chart: "Chart",
  calculation: "Calculation",
  dashboard: "Dashboard",
  illustration: "Illustration",
  photo: "Photo",
  interactive: "Interactive",
};

export function GenericLessonVisual({ visual }: { visual: LessonVisual }) {
  const items = extractItems(visual);
  const label = TYPE_LABEL[visual.type] ?? "Visual";

  if (items.length === 2 && visual.type === "comparison") {
    return (
      <div className="generic-visual generic-visual-comparison" role="img" aria-label={visual.altText}>
        <span className="generic-visual-label">{label}</span>
        {items.map((item) => (
          <div className="generic-visual-column" key={item}>
            <p>{item}</p>
          </div>
        ))}
      </div>
    );
  }

  if (items.length >= 2) {
    return (
      <div className="generic-visual generic-visual-flow" role="img" aria-label={visual.altText}>
        <span className="generic-visual-label">{label}</span>
        <div className="generic-visual-steps">
          {items.map((item, index) => (
            <div className="generic-visual-step-wrap" key={item}>
              <div className="generic-visual-step">
                <span className="generic-visual-step-index">{index + 1}</span>
                <p>{item}</p>
              </div>
              {index < items.length - 1 && (
                <span className="generic-visual-arrow" aria-hidden="true">
                  &rarr;
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="generic-visual generic-visual-summary" role="img" aria-label={visual.altText}>
      <span className="generic-visual-label">{label}</span>
      <h3>{visual.title}</h3>
      <p>{visual.purpose}</p>
    </div>
  );
}
